/**
 * Стандартные виды модуля 3D (как в CAD):
 * слева / справа / спереди / сзади / сверху / снизу — плоские орто-проекции (без вращения);
 * аксонометрия — объёмный орто-вид (изометрия);
 * перспектива — объёмный вид с перспективой и свободным вращением.
 *
 * Центр объёма: (0, height/2, 0). Оси: width→X, height→Y, depth→Z.
 */

export const CAMERA_VIEW = {
    LEFT: "left",
    RIGHT: "right",
    FRONT: "front",
    BACK: "back",
    TOP: "top",
    BOTTOM: "bottom",
    AXONOMETRY: "axonometry",
    PERSPECTIVE: "perspective",
};

export const CAMERA_VIEW_OPTIONS = [
    {id: CAMERA_VIEW.LEFT, label: "Слева"},
    {id: CAMERA_VIEW.RIGHT, label: "Справа"},
    {id: CAMERA_VIEW.FRONT, label: "Спереди"},
    {id: CAMERA_VIEW.BACK, label: "Сзади"},
    {id: CAMERA_VIEW.TOP, label: "Сверху"},
    {id: CAMERA_VIEW.BOTTOM, label: "Снизу"},
    {id: CAMERA_VIEW.AXONOMETRY, label: "Аксонометрия"},
    {id: CAMERA_VIEW.PERSPECTIVE, label: "Перспектива"},
];

/** Плоские ortho-виды: только pan/zoom, без rotate. */
export const isPlanarOrthoView = (view) =>
    view === CAMERA_VIEW.LEFT
    || view === CAMERA_VIEW.RIGHT
    || view === CAMERA_VIEW.FRONT
    || view === CAMERA_VIEW.BACK
    || view === CAMERA_VIEW.TOP
    || view === CAMERA_VIEW.BOTTOM;

/** Ортографическая проекция (все виды кроме перспективы). */
export const isOrthographicView = (view) => view !== CAMERA_VIEW.PERSPECTIVE;

const fitHalf = (a, b, pad = 1.3) => Math.max(0.5 * Math.max(a, b) * pad, 0.15);

/**
 * Половина высоты ortho-frustum (в мировых метрах) до учёта aspect.
 * Ширина = half * aspect; при смене вида zoom сбрасывается в 1.
 */
export const getOrthoFrustumHalf = (sizeM, view = CAMERA_VIEW.AXONOMETRY) => {
    const width = Math.max(Number(sizeM?.width) || 1, 0.05);
    const height = Math.max(Number(sizeM?.height) || 1, 0.05);
    const depth = Math.max(Number(sizeM?.depth) || 1, 0.05);

    switch (view) {
        case CAMERA_VIEW.LEFT:
        case CAMERA_VIEW.RIGHT:
            return fitHalf(height, depth);
        case CAMERA_VIEW.FRONT:
        case CAMERA_VIEW.BACK:
            return fitHalf(width, height);
        case CAMERA_VIEW.TOP:
        case CAMERA_VIEW.BOTTOM:
            return fitHalf(width, depth);
        case CAMERA_VIEW.AXONOMETRY:
        case CAMERA_VIEW.PERSPECTIVE:
        default:
            return fitHalf(Math.max(width, depth), height, 1.55);
    }
};

const isometricPosition = (target, dist) => {
    const inv = 1 / Math.sqrt(3);
    // Камера с +X/+Z: угол origin (−X, пол, −Z) — дальний, диагонально напротив.
    return [
        target[0] + dist * inv,
        target[1] + dist * inv,
        target[2] + dist * inv,
    ];
};

/**
 * @param {{width:number,height:number,depth:number}} sizeM
 * @param {string} view
 * @returns {{
 *   position:[number,number,number],
 *   target:[number,number,number],
 *   up:[number,number,number],
 *   frustumHalf:number,
 * }}
 */
export const getCameraViewPose = (sizeM, view = CAMERA_VIEW.AXONOMETRY, options = {}) => {
    const width = Math.max(Number(sizeM?.width) || 1, 0.05);
    const height = Math.max(Number(sizeM?.height) || 1, 0.05);
    const depth = Math.max(Number(sizeM?.depth) || 1, 0.05);
    const extent = Math.max(width, height, depth);
    const distanceScale = Number(options.distanceScale) > 0 ? Number(options.distanceScale) : 2.5;
    const dist = Math.max(extent * distanceScale, 1.5);
    const cy = height / 2;
    const target = [0, cy, 0];
    const upY = [0, 1, 0];
    const frustumHalf = getOrthoFrustumHalf(sizeM, view);

    switch (view) {
        case CAMERA_VIEW.RIGHT:
            return {position: [dist, cy, 0], target, up: upY, frustumHalf};
        case CAMERA_VIEW.LEFT:
            return {position: [-dist, cy, 0], target, up: upY, frustumHalf};
        case CAMERA_VIEW.FRONT:
            return {position: [0, cy, dist], target, up: upY, frustumHalf};
        case CAMERA_VIEW.BACK:
            return {position: [0, cy, -dist], target, up: upY, frustumHalf};
        case CAMERA_VIEW.TOP:
            return {
                position: [0, cy + dist, 0],
                target,
                up: [0, 0, -1],
                frustumHalf,
            };
        case CAMERA_VIEW.BOTTOM:
            return {
                position: [0, cy - dist, 0],
                target,
                up: [0, 0, 1],
                frustumHalf,
            };
        case CAMERA_VIEW.PERSPECTIVE:
        case CAMERA_VIEW.AXONOMETRY:
        default:
            return {
                position: isometricPosition(target, dist),
                target,
                up: upY,
                frustumHalf,
            };
    }
};

/**
 * @param {import('three').OrthographicCamera} camera
 * @param {{target: import('three').Vector3, object?: import('three').Object3D, update: Function}|null} controls
 * @param {ReturnType<typeof getCameraViewPose>} pose
 * @param {number} aspect
 */
export const applyOrthoCameraView = (camera, controls, pose, aspect = 1) => {
    if (!camera?.isOrthographicCamera || !pose) return;

    const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
    const halfH = pose.frustumHalf;
    const halfW = halfH * safeAspect;

    camera.left = -halfW;
    camera.right = halfW;
    camera.top = halfH;
    camera.bottom = -halfH;
    camera.zoom = 1;
    camera.up.set(pose.up[0], pose.up[1], pose.up[2]);
    camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
    camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);
    camera.updateProjectionMatrix();

    if (controls) {
        controls.target.set(pose.target[0], pose.target[1], pose.target[2]);
        if (controls.object) {
            controls.object.up.copy(camera.up);
        }
        controls.update();
    }
};

/**
 * @param {import('three').PerspectiveCamera} camera
 * @param {{target: import('three').Vector3, object?: import('three').Object3D, update: Function}|null} controls
 * @param {ReturnType<typeof getCameraViewPose>} pose
 */
export const applyPerspectiveCameraView = (camera, controls, pose) => {
    if (!camera?.isPerspectiveCamera || !pose) return;

    camera.up.set(pose.up[0], pose.up[1], pose.up[2]);
    camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
    camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);
    camera.updateProjectionMatrix();

    if (controls) {
        controls.target.set(pose.target[0], pose.target[1], pose.target[2]);
        if (controls.object) {
            controls.object.up.copy(camera.up);
        }
        controls.update();
    }
};
